import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { releaseParkedEvents } from "@/anticheat";
import { useContentProtection } from "@/anticheat/react/useContentProtection";
import { useSnapshotSchedule } from "@/anticheat/react/useSnapshotSchedule";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Coffee,
  Loader2,
} from "lucide-react";

import {
  isFullscreen,
  isFullscreenSupported,
  requestFullscreen,
} from "@/anticheat/fullscreen";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CameraPreview } from "@/components/candidate/CameraPreview";
import {
  CAMERA_TILE_FOOTER_CLEAR,
  CAMERA_TILE_RESERVE,
  snapshotCaption,
  useCameraTileShown,
} from "@/components/candidate/cameraTile";
import { ReportProblemTrigger } from "@/components/candidate/ReportProblemDialog";
import {
  ADVANCE_FAILED_MESSAGE,
  SUBMIT_FAILED_MESSAGE,
  SubmitConfirmDialog,
  TIME_UP_CLOSE_FAILED_MESSAGE,
} from "@/components/candidate/SubmitConfirmDialog";
import { ReviewCard } from "@/routes/Review";
import {
  advanceSection,
  bounceToGate,
  fetchReview,
  finishSection,
  loadAttemptId,
  loadCandidateMeta,
  submitAttempt,
  type FinishSectionResponse,
  type ReviewResponse,
} from "@/lib/api";
import { resolveAnticheat } from "@/lib/anticheatConfig";
import { AnswerSaveFailureNote } from "@/components/candidate/AnswerSaveFailureNote";
import { flushAnswerSaves } from "@/lib/answerQueue";
import { useAnswerSaveFailures } from "@/lib/useAnswerSaveFailures";
import { flushReplayUploads } from "@/lib/codeReplayRecorder";
import {
  classifyAdvanceError,
  isAttemptGone,
  isFinalizedAttempt,
  pausedAttemptOf,
} from "@/lib/candidateError";
import { queryKeys } from "@/lib/queryKeys";
import { submitSummary } from "@/lib/submitConfirm";
import { useCountdown } from "@/lib/useCountdown";
import { cn, formatHMS, formatMMSS } from "@/lib/utils";
import { pausedStateOf } from "./Paused";

// Per-section review (MVP 3.0 locked-sequential sections). Reached from
// Question.tsx when the candidate clicks "Finish section" on the last question
// of a section. Lists ONLY that section's questions (so the locked-once-left
// rule holds — no jump-back across sections), with its own per-section
// countdown. "Finish section & continue" advances to the next section; on the
// last section it submits the whole attempt.
//
// CP-18 gives this screen a second state. With `break_between_sections` on the
// test, the primary action is "Finish section & take a break" → POST
// /finish-section → the attempt is PAUSED server-side (`paused`): the clock is
// hidden (a shared clock shows its frozen remaining), jump-back is closed, and
// the time-up effect is off. "Begin Section N" → /advance-section ends the
// break; the server measures it. No anti-cheat controller is mounted here
// (that all lives in Question.tsx), so this screen emits no tab / fullscreen /
// camera events by construction. The one thing that does carry on while the
// clock runs is the attempt's 30 s snapshot schedule (EF-8, photo cadence
// 2026-10-10) — held below, and released on a break.
//
// The section range, index and deadline arrive via router state (set by
// Question.tsx) to avoid an extra fetch. A direct navigation with no state
// (new tab, pasted URL) bounces to the gate.
//
// Since FRONTEND-REACT-8 (2026-08-25) the gate resumes a live in-flight attempt
// — but NOT for this bounce, which is deliberately tagged `state.from="bounce"`.
// The gate always resumes at question 1, and this bounce fires precisely when
// the candidate is somewhere a plain question-1 redirect cannot represent, so
// resuming would ping-pong (gate -> /question/1 -> section_locked -> gate).
// Tagged bounces therefore fall through to the normal gate and this stays the
// DEAD END it has always been for an in-progress attempt: it must remain as hard
// to reach as it is today, and nothing here may start treating a missing field
// as missing state. (A PAUSED attempt is the exception that now works: the
// question route's `section_paused` 409 carries the bounds and lands here WITH
// state — see Question.tsx.)
interface SectionReviewState {
  sectionStart: number;
  sectionEnd: number;
  sectionIndex: number;
  isLast: boolean;
  // This section's deadline, handed over by the screen that owned its clock
  // (BUG-5). OPTIONAL ON PURPOSE: a history entry written by a pre-BUG-5 bundle
  // — the candidate refreshes this page across a deploy — has no expiresAt, and
  // treating it as required would trip the dead-end bounce above.
  expiresAt?: string;
  // CP-18. `paused` is written into history by the finish mutation (and by
  // Question.tsx when the clock or a paused-tab 409 lands here) so a RELOAD
  // keeps the break: the time-up effect must never act off a frozen deadline,
  // and it runs before GET /review can say "paused". The two flags let that
  // effect choose finish / submit / advance before the payload lands too.
  paused?: boolean;
  sharedClock?: boolean;
  breakBetweenSections?: boolean;
}

export default function SectionReview() {
  const { token = "demo" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const attemptId = loadAttemptId(token);
  const state = location.state as SectionReviewState | null;
  // Test name for the header before GET /review lands — sessionStorage, written
  // by Gate.tsx at /start and cleared only on Done. Memoized so the 1 Hz
  // countdown re-render doesn't re-parse it.
  const meta = useMemo(() => loadCandidateMeta(token), [token]);
  // FEAT-17: the prompt previews are question content too.
  useContentProtection(Boolean(attemptId));

  // Pre-ship R7: no anti-cheat controller lives here to adopt what the last
  // question parked (anticheat `parked`, a 3 s hand-off), and a Submit inside
  // that window graded the attempt — verdict, webhook, Traffit note — before
  // those events landed. Ship them on arrival, as dispose() did before parking.
  useEffect(() => {
    releaseParkedEvents();
  }, []);

  useEffect(() => {
    if (!attemptId) {
      navigate(`/t/${token}`, { replace: true, state: { from: "bounce" } });
      return;
    }
    // No section context at all (direct/stale URL) — nothing here can render, so
    // bounce to the gate. See the note on SectionReviewState: this is a dead end
    // for an in-progress attempt, so it stays reserved for a genuinely absent
    // state object, never for an individual missing field.
    if (!state) {
      navigate(`/t/${token}`, { replace: true, state: { from: "bounce" } });
    }
  }, [attemptId, navigate, state, token]);

  // Drain background answer saves before loading this section's review, so the
  // per-question submitted/pending state is accurate (the save queue survives the
  // Question→SectionReview unmount).
  const [savesFlushed, setSavesFlushed] = useState(false);
  useEffect(() => {
    let alive = true;
    void flushAnswerSaves().finally(() => {
      if (alive) setSavesFlushed(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const { data, isLoading, error } = useQuery<ReviewResponse>({
    // Section-scoped (BUG-5) — GET /review returns the whole attempt, but this
    // screen renders one section of it, so the previous section's payload must
    // never be a legal cache hit here. The -1 sentinel is unreachable in
    // practice (no state ⇒ this component bounces and returns null); it exists
    // so that render can't emit the 2-arg key and collide with Review.tsx's
    // global entry.
    queryKey: queryKeys.review(token, attemptId, state?.sectionIndex ?? -1),
    queryFn: () => fetchReview(token, attemptId!),
    enabled: Boolean(attemptId) && Boolean(state) && savesFlushed,
    staleTime: 5_000,
  });

  // Submitted/expired → Done. Classified at render rather than only inside the
  // effect because the guards below need it too: the redirect lives in an effect
  // that runs after paint, so without this the load-failure card would flash for
  // one frame on the way out.
  // Classify on status (ApiError 409/410), never on the formatted message text:
  // a non-terminal 5xx/404 whose body merely CONTAINS "409"/"410" (attempt ids
  // are hex UUIDs) must not strand a live candidate on Done. See isFinalizedAttempt.
  const attemptFinalized = isFinalizedAttempt(error);
  // 10-07 S11: voided (an invitation replaced this link attempt, or a retake)
  // or erased — the stored id is junk; back to the Gate, never the load card.
  const attemptGone = isAttemptGone(error);

  useEffect(() => {
    if (!error || !attemptFinalized) return;
    navigate(`/t/${token}/done`, { replace: true });
  }, [error, attemptFinalized, navigate, token]);

  useEffect(() => {
    if (!attemptGone) return;
    bounceToGate(token, navigate);
  }, [attemptGone, navigate, token]);

  // EF-2: a stale review tab on a PAUSED attempt gets 409 `attempt_paused`
  // (excluded from `isFinalizedAttempt` above) — show the paused screen,
  // rebuilt from the 409, never Done. Keyed on `error`, so it fires once.
  useEffect(() => {
    const paused = pausedAttemptOf(error);
    if (!paused) return;
    navigate(`/t/${token}/paused`, {
      replace: true,
      state: pausedStateOf(paused),
    });
  }, [error, navigate, token]);

  // CP-18 break state. Router state first (it survives a reload and is written
  // the moment the finish lands), then the server's word from /review (a reload
  // whose history entry predates the finish), then the fresh finish response
  // for the first frame after the click.
  const [finishResult, setFinishResult] = useState<FinishSectionResponse | null>(
    null,
  );
  const paused = Boolean(state?.paused || data?.paused || finishResult?.paused);
  const sharedClock = state?.sharedClock ?? data?.shared_clock ?? false;
  const breakEnabled =
    state?.breakBetweenSections ?? data?.break_between_sections ?? false;
  // Shared clock only: what the candidate has left once the break ends, frozen
  // by the server at Finish. Rendered directly — never through useCountdown,
  // which would keep ticking a deadline that is not moving.
  const breakRemainingSeconds =
    finishResult?.remaining_seconds ?? data?.break_remaining_seconds ?? null;
  // Sheet #29: an invitation link brings the candidate back to this screen
  // after the tab is closed (the server hands the attempt back on /preview);
  // a shared link cannot, so the line is shown only when the server says so.
  // Read ONLY off a /review answer taken on the break: the one cached from
  // before "Finish section" says false for every link (there was no break
  // yet), and until the refetch landed it told an invitation candidate to
  // keep the tab open. null = no such answer yet, so neither line.
  const canReturnByLink: boolean | null = data?.paused
    ? data.break_return_by_link === true
    : null;

  // Per-section countdown. Router state FIRST (BUG-5): it is on screen from the
  // very first frame and is constant for this screen's whole life — the backend
  // only moves attempt.expires_at at /start and /advance-section — so the clock
  // can never re-base mid-view. The payload is a fallback for pre-BUG-5 history
  // entries only; it is now safe too, since the query key is section-scoped.
  // Paused (CP-18): no deadline at all — the clock is stopped and never shown.
  const sectionExpiresAt = paused ? null : (state?.expiresAt ?? data?.expires_at);
  const remainingMs = useCountdown(sectionExpiresAt);

  // EF-8 (photo cadence): while the section clock runs here the attempt's ONE
  // 30 s snapshot schedule carries on, held from this page as the question
  // page holds it (the frozen config is the tab's meta; GET /review has
  // none). A break releases it — the schedule pauses in place and "Begin
  // Section N" → Question resumes it, without a burst unless a photo was due.
  const snapshots = meta?.camera_snapshots ?? null;
  const photosRun = snapshots !== null && !paused;
  useSnapshotSchedule(token, attemptId, snapshots, !paused);
  const tileShown = useCameraTileShown() && photosRun;

  // FEAT-15: on the LAST section the primary button is the final submit, so it
  // asks first. Every other press of it (next section, break, Begin) and every
  // clock-driven transition goes straight through, as before.
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // R17 (pre-ship 2026-10-18): a failed NON-final transition (the next
  // section, the break) used to set only the confirm's line — not on screen
  // for it — so the button just re-enabled with no message. And a failed
  // clock-driven one never retried: its latch stayed set. Both now say so
  // under the header, with a Retry (as Question.tsx's).
  const [advanceError, setAdvanceError] = useState<string | null>(null);
  const [timeUpError, setTimeUpError] = useState<string | null>(null);

  // OPS-28 #1: answers the queue gave up on. Held on EVERY press of the
  // primary button, not only the final submit: continuing locks this section
  // (advance) or grades it (submit), and either way a kept failure in it is
  // lost for good. The clock-driven transitions are not held — the clock
  // decided, and the pre-deadline save carried what it could. Only THIS
  // section's failures count: an earlier section is locked, its saves can
  // never land, and holding this button on them would never clear.
  const saveFailures = useAnswerSaveFailures(
    attemptId,
    state ? { start: state.sectionStart, end: state.sectionEnd } : null,
  );

  const continueMutation = useMutation({
    // `timeUp`: the clock drove it (the effect below), not a click — R17.
    mutationFn: async (vars: { forceSubmit?: boolean; timeUp?: boolean }) => {
      if (!state) throw new Error("missing section state");
      // Land this section's background saves before locking it (advance) or
      // grading it (submit) — otherwise a still-queued answer could be dropped.
      await flushAnswerSaves();
      // FEAT-31: and the section's last code-replay chunk (bounded, never throws).
      await flushReplayUploads();
      // `forceSubmit` (CP-18): under a shared clock the deadline that lapsed on
      // this screen is the whole test's, whatever section we are on.
      if (state.isLast || vars.forceSubmit) {
        await submitAttempt(token, attemptId!);
        return { done: true as const };
      }
      const resp = await advanceSection(token, attemptId!, state.sectionIndex);
      return { done: false as const, firstPosition: resp.first_position };
    },
    onSuccess: (res) => {
      if (res.done) {
        navigate(`/t/${token}/done`, { replace: true });
      } else {
        navigate(`/t/${token}/question/${res.firstPosition}`, { replace: true });
      }
    },
    // A finalized/expired attempt converges to Done. Classify on status + reason
    // (ApiError), never on the message text — and a 409 `section_paused` from
    // /submit leaves a LIVE attempt behind, so it stays put.
    onError: (e, vars) => {
      const action = classifyAdvanceError(e);
      if (action === "done") {
        navigate(`/t/${token}/done`, { replace: true });
        return;
      }
      if (action === "clear-and-bounce") {
        bounceToGate(token, navigate);
        return;
      }
      if (vars.timeUp) {
        transitionFailedOnTimeUp();
        return;
      }
      // FEAT-15: still a live attempt — the final submit says so in the
      // confirm, where the candidate can press Submit again; any other
      // transition under the header (R17), where it is on screen.
      if (state?.isLast) setSubmitError(SUBMIT_FAILED_MESSAGE);
      else setAdvanceError(ADVANCE_FAILED_MESSAGE);
    },
  });

  // CP-18: close this section and start the break.
  const finishMutation = useMutation({
    mutationFn: async (_vars: { timeUp?: boolean }) => {
      if (!state) throw new Error("missing section state");
      // Every queued answer must be on the server BEFORE the pause: a save
      // landing afterwards is refused (409 section_paused) and answerQueue
      // drops it. The mount flush above is not enough — a jump-back edit
      // re-enqueues after it.
      await flushAnswerSaves();
      // FEAT-31: a replay chunk landing during the break is refused too.
      await flushReplayUploads();
      return finishSection(token, attemptId!, state.sectionIndex);
    },
    onSuccess: (res) => {
      if (!state) return;
      if (!res.paused) {
        // Stale tab: the server already moved on to a later section. Follow it.
        navigate(`/t/${token}/question/${res.first_position}`, { replace: true });
        return;
      }
      setFinishResult(res);
      // Write the break into history so a reload lands paused (router state
      // survives reload), and DROP the outgoing deadline — the time-up effect
      // must never fire off a clock that is no longer running.
      const next: SectionReviewState = {
        ...state,
        paused: true,
        expiresAt: undefined,
        sharedClock,
        breakBetweenSections: true,
      };
      navigate(location.pathname, { replace: true, state: next });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.review(token, attemptId, state.sectionIndex),
      });
    },
    onError: (e, vars) => {
      // Only a genuinely finalized attempt goes to Done. A paused / disabled /
      // mismatch 409 leaves a live attempt behind — stay on this screen, and
      // say so (R17: it used to be silent).
      const action = classifyAdvanceError(e);
      if (action === "done") {
        navigate(`/t/${token}/done`, { replace: true });
      } else if (action === "clear-and-bounce") {
        bounceToGate(token, navigate);
      } else if (vars.timeUp) {
        transitionFailedOnTimeUp();
      } else {
        setAdvanceError(ADVANCE_FAILED_MESSAGE);
      }
    },
  });

  // Time up: transition without the review gate (the clock decided). Latch so
  // the timer + a manual click can't double-fire the transition.
  //
  // This may act ONLY on the deadline handed over by Question.tsx, never on the
  // /review payload's expires_at — even though that payload is now section-
  // scoped. The distinction is provenance, not value: this effect can advance a
  // section or SUBMIT THE WHOLE ATTEMPT, so it must not be drivable by anything
  // a cache could ever mis-serve. Before BUG-5 it was — the stale payload
  // carried the PREVIOUS section's (usually already past) deadline, so
  // remainingMs was 0 on the first tick and this fired on mount, auto-advancing
  // or auto-submitting before the candidate saw this screen at all.
  //
  // On a pre-BUG-5 history entry (no expiresAt) the clock still renders from the
  // payload, but only the candidate's own click ends the section. The server
  // stays authoritative either way: /advance-section deliberately allows a late
  // advance, and /submit returns the friendly time-up 410.
  //
  // CP-18: never while paused — the clock is stopped, whatever a stale
  // `expiresAt` says. Under a shared clock the lapse is the whole test's →
  // submit; with breaks on, the section closes into a break instead of the next
  // section starting on its own.
  //
  // Runs while the body is still loading, which is correct — the mutations
  // read only `state` and `attemptId`, never `data`.
  //
  // FEAT-11 (R4, night-p5 2026-10-08): on a MIXED attempt the clock that ran
  // out is the technical half's and a clockless questionnaire is still owed —
  // hand the candidate on, exactly as Question.tsx's handleTimeUp and
  // Review.tsx do. Checked first because it applies in every timing mode,
  // including the last technical section (`isLast` is False there) and a
  // shared clock, where the force-submit below would be refused (410
  // `technical_time_up` → "stay") and leave them on a screen whose clock reads
  // zero. Needs `data`; before it lands the server-side branches carry it —
  // /advance-section and /finish-section both answer the boundary there.
  const questionnaireStartsAt = data?.questionnaire_starts_at ?? null;
  const timeUpRef = useRef(false);
  // R17: a failed clock-driven transition re-arms the latch and waits for the
  // candidate's Retry. Gated on the error, never a bare re-arm — the mutation
  // objects are this effect's deps, so a re-armed latch alone would re-fire
  // it on the very render the failure caused, for as long as it fails.
  function transitionFailedOnTimeUp() {
    timeUpRef.current = false;
    setTimeUpError(TIME_UP_CLOSE_FAILED_MESSAGE);
  }
  useEffect(() => {
    if (!state?.expiresAt || paused) return;
    if (timeUpError) return; // R17: wait for the candidate's Retry
    if (remainingMs !== 0 || timeUpRef.current) return;
    timeUpRef.current = true;
    if (questionnaireStartsAt != null) {
      // Queued technical saves land first (never rejects): a technical answer
      // arriving after the first statement is answered is refused.
      void flushAnswerSaves().then(() =>
        navigate(`/t/${token}/question/${questionnaireStartsAt}`, { replace: true }),
      );
    } else if (state.isLast || state.sharedClock) {
      continueMutation.mutate({ forceSubmit: true, timeUp: true });
    } else if (state.breakBetweenSections) {
      finishMutation.mutate({ timeUp: true });
    } else {
      continueMutation.mutate({ timeUp: true });
    }
  }, [
    remainingMs,
    state,
    paused,
    timeUpError,
    continueMutation,
    finishMutation,
    questionnaireStartsAt,
    navigate,
    token,
  ]);

  if (!attemptId || !state) return null;

  const busy = continueMutation.isPending || finishMutation.isPending;

  // Hoisted out of the branches below so the chrome — above all the live
  // countdown — stays on screen while the body loads. Section-scoping the query
  // key (BUG-5) means this screen now starts from a cold cache, and a candidate
  // under time pressure must never watch their clock blink out and reappear.
  const header = (
    <header className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
      <div className="text-sm">
        <span className="font-medium text-foreground">
          {paused ? "Break" : "Review section"}
        </span>
        <span className="ml-2 text-muted-foreground">
          {data?.test_name ?? meta?.test_name ?? ""}
        </span>
      </div>
      {paused ? (
        sharedClock &&
        breakRemainingSeconds !== null && (
          <div
            className="flex items-center gap-1.5 font-mono text-sm tabular-nums text-muted-foreground"
            role="timer"
            aria-label="Time remaining (paused)"
          >
            <Clock className="h-4 w-4" aria-hidden="true" />
            {formatHMS(breakRemainingSeconds * 1000)}
            <span className="font-sans text-4xs uppercase tracking-wide">
              paused
            </span>
          </div>
        )
      ) : (
        remainingMs !== null && (
          <div
            className={cn(
              "flex items-center gap-1.5 font-mono text-sm tabular-nums",
              remainingMs <= 60_000
                ? "font-semibold text-destructive"
                : "text-muted-foreground",
            )}
            role="timer"
            aria-label={sharedClock ? "Time remaining" : "Section time remaining"}
          >
            <Clock className="h-4 w-4" aria-hidden="true" />
            {sharedClock ? formatHMS(remainingMs) : formatMMSS(remainingMs)}
          </div>
        )
      )}
    </header>
  );

  // Error guard FIRST. `isLoading` is false once a fetch has failed, but `data`
  // is undefined on a cold key — under the old order (loading guard first) that
  // combination returned the spinner forever and made this card unreachable,
  // stranding the candidate mid-test with the footer button out of reach too.
  // Section-scoping the key is what makes a cold key reachable here at all.
  // `attemptFinalized` is carved out because the effect above is already
  // redirecting to Done; without it this card paints for one frame on the way.
  // FEAT-15 verify (2026-10-03): and only on a cold key — a failed background
  // refetch over a section already on screen keeps it, and the last section's
  // submit confirm with it (same rule as Review.tsx).
  if (attemptGone) return null;
  if (error && !attemptFinalized && !data && !busy) {
    return (
      <div className="flex h-full flex-col bg-background">
        {header}
        <div className="flex flex-1 items-center justify-center p-6">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Couldn't load this section</CardTitle>
              <CardDescription>
                Try refreshing. If the issue persists, contact the recruiter.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex h-full flex-col bg-background">
        {header}
        <div className="flex flex-1 items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Only this section's questions (1-based positions in [start, end]).
  const sectionAnswers = data.answers.filter(
    (a) => a.position >= state.sectionStart && a.position <= state.sectionEnd,
  );
  const total = sectionAnswers.length;
  const submitted = sectionAnswers.filter((a) => a.submitted).length;
  const unsubmitted = total - submitted;
  const allAnswered = submitted === total;
  // Whole minutes for the next-section briefing (matches the recruiter's input,
  // which is authored in minutes; clamp to ≥1 so a sub-minute limit never reads
  // 0). 0 under a shared clock — the next section has no time of its own.
  const nextSectionMinutes =
    data.next_section && data.next_section.time_limit_seconds != null
      ? Math.max(1, Math.round(data.next_section.time_limit_seconds / 60))
      : 0;
  const nextNumber = (data.next_section?.index ?? state.sectionIndex + 1) + 1;
  // Jump-back is a review affordance; a finished (paused) section is closed —
  // the server 409s every read/write into it anyway.
  const canJumpBack = data.allow_back_navigation && !paused;

  // FEAT-11 (R4, night-p5): on a mixed attempt the last TECHNICAL section has
  // no next section and `isLast` False — what follows is the clockless
  // questionnaire. A break "before" it is meaningless, and until 2026-10-08
  // the "take a break" press posted /finish-section, got 409 `no_next_section`
  // and was classified as Done — the candidate was dumped on the Done screen
  // with the questionnaire uncollected. Offer the plain continue instead;
  // /advance-section hands back the boundary as `first_position`.
  const questionnaireNext = !data.next_section && questionnaireStartsAt != null;

  let primaryLabel: ReactNode;
  if (continueMutation.isPending) {
    primaryLabel = (
      <>
        <Loader2 className="h-4 w-4 animate-spin" />
        {state.isLast ? "Submitting…" : "Starting…"}
      </>
    );
  } else if (finishMutation.isPending) {
    primaryLabel = (
      <>
        <Loader2 className="h-4 w-4 animate-spin" />
        Finishing…
      </>
    );
  } else if (state.isLast) {
    primaryLabel = "Submit final";
  } else if (paused) {
    primaryLabel = `Begin Section ${nextNumber}`;
  } else if (breakEnabled && !questionnaireNext) {
    primaryLabel = "Finish section & take a break";
  } else if (data.next_section) {
    primaryLabel = `Begin Section ${nextNumber}`;
  } else {
    primaryLabel = "Finish section & continue";
  }

  const onPrimary = () => {
    if (state.isLast) {
      setSubmitError(null);
      setSubmitConfirmOpen(true);
      return;
    }
    setAdvanceError(null);
    if (paused) {
      // The candidate may well have left fullscreen during the break. Re-enter
      // on THIS gesture (best-effort, like the gate's Begin) so the question
      // route doesn't greet them with the blocking overlay. Synchronous with
      // the click — the activation token must be banked before any await.
      // FEAT-25: only on an attempt that froze the fullscreen check on (the
      // Gate cached it at /start; absent = on).
      if (
        resolveAnticheat(meta?.anticheat).fullscreen &&
        isFullscreenSupported() &&
        !isFullscreen()
      ) {
        try {
          void requestFullscreen().catch(() => {});
        } catch {
          // Unsupported / refused — the overlay on the next question handles it.
        }
      }
      continueMutation.mutate({});
      return;
    }
    if (breakEnabled && !questionnaireNext) { // night-p5 R4
      finishMutation.mutate({});
      return;
    }
    continueMutation.mutate({});
  };

  return (
    <div className="flex h-full flex-col bg-background">
      {snapshots && !paused && (
        <CameraPreview caption={snapshotCaption(snapshots.interval_s)} />
      )}
      {submitConfirmOpen && state.isLast && (
        <SubmitConfirmDialog
          // The WHOLE test, not this section: the submit ends all of it.
          summary={submitSummary(data.answers)}
          // Same formatter as the header clock, so the two agree.
          timeLeft={
            remainingMs !== null
              ? sharedClock
                ? formatHMS(remainingMs)
                : formatMMSS(remainingMs)
              : null
          }
          onConfirm={() => continueMutation.mutate({})}
          onCancel={() => setSubmitConfirmOpen(false)}
          isPending={continueMutation.isPending}
          error={submitError}
        />
      )}
      {header}
      {timeUpError && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 border-b border-destructive/30 bg-destructive/5 px-6 py-2 text-sm"
        >
          <span className="text-destructive">{timeUpError}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            // Clearing the error re-runs the time-up effect (the latch is
            // already re-armed), which retries the same transition.
            onClick={() => setTimeUpError(null)}
            disabled={busy}
            className="cursor-pointer"
          >
            Retry
          </Button>
        </div>
      )}
      {advanceError && !timeUpError && (
        <div
          role="alert"
          className="border-b border-destructive/30 bg-destructive/5 px-6 py-2 text-sm text-destructive"
        >
          {advanceError}
        </div>
      )}

      {/* Room under the last card and a clear footer while the camera tile
          (fixed bottom-right) is on screen — see Review.tsx. */}
      <main
        className={cn(
          "mx-auto w-full max-w-3xl flex-1 space-y-6 px-6 py-8",
          tileShown && CAMERA_TILE_RESERVE,
        )}
      >
        {paused && (
          <div className="flex items-start gap-3 rounded-md border border-info/40 bg-info/5 p-4">
            <Coffee className="mt-0.5 h-5 w-5 flex-shrink-0 text-info" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                Section {state.sectionIndex + 1} is finished &mdash; take a break.
              </p>
              <p className="text-sm text-muted-foreground">
                The clock is stopped.{" "}
                {sharedClock && breakRemainingSeconds !== null
                  ? `You have ${formatHMS(breakRemainingSeconds * 1000)} left for the rest of the test; it resumes when you press "Begin Section ${nextNumber}".`
                  : `Section ${nextNumber} has its own clock, which starts when you press "Begin Section ${nextNumber}".`}
              </p>
              {/* ⚠ Placeholder copy — the wording is Emilia's. Rendered once
                  /review has answered ON the break: until then nothing is
                  promised either way. */}
              <p className="text-xs text-muted-foreground">
                Take as long as you need. You can leave this page open and come
                back to it.
                {canReturnByLink !== null &&
                  (canReturnByLink
                    ? " You can also close it: the link in your invitation email brings you back here, with your answers saved and the clock still stopped."
                    : " Keep this tab open — the test link can't bring you back to a test that is already under way.")}
              </p>
            </div>
          </div>
        )}

        <AnswerSaveFailureNote
          failures={saveFailures.failures}
          refused={saveFailures.refused}
          onRetry={() =>
            void saveFailures.retry().then(() =>
              queryClient.invalidateQueries({
                queryKey: queryKeys.review(token, attemptId, state.sectionIndex),
              }),
            )
          }
          retrying={saveFailures.retrying}
        />
        <div
          className={cn(
            "flex items-start gap-3 rounded-md border p-4",
            allAnswered
              ? "border-success/40 bg-success/5"
              : "border-destructive/30 bg-destructive/5",
          )}
        >
          {allAnswered ? (
            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-success" />
          ) : (
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
          )}
          <div className="space-y-1">
            <p
              className={cn(
                "text-sm font-medium",
                allAnswered ? "text-success" : "text-destructive",
              )}
            >
              {allAnswered
                ? `You've submitted all ${total} questions in this section.`
                : `${submitted} of ${total} submitted — ${unsubmitted} pending.`}
            </p>
            <p className="text-xs text-muted-foreground">
              {canJumpBack
                ? "Jump back to any question in this section to finish it. "
                : ""}
              <span className="font-medium text-foreground">
                {paused
                  ? "This section is now closed."
                  : "Once you continue you can't return to this section."}
              </span>
            </p>
          </div>
        </div>

        {!state.isLast && data.next_section && (
          <div className="flex items-start gap-3 rounded-md border border-info/40 bg-info/5 p-4">
            <ArrowRight className="mt-0.5 h-5 w-5 flex-shrink-0 text-info" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                You&rsquo;re about to begin Section {data.next_section.index + 1}
                {data.next_section.name ? ` — ${data.next_section.name}` : ""}.
              </p>
              <p className="text-sm text-muted-foreground">
                This section contains {data.next_section.question_count} question
                {data.next_section.question_count === 1 ? "" : "s"}
                {sharedClock
                  ? ", and your remaining test time carries over."
                  : ` and you'll have ${nextSectionMinutes} minute${
                      nextSectionMinutes === 1 ? "" : "s"
                    } to complete ${
                      data.next_section.question_count === 1 ? "it" : "them"
                    }.`}
              </p>
              <p className="text-xs text-muted-foreground">
                {paused
                  ? `The clock ${sharedClock ? "resumes" : "starts"} when you press "Begin Section ${nextNumber}".`
                  : breakEnabled
                    ? sharedClock
                      ? "Finish the section to take a break — the clock stops until you press Begin."
                      : `Finish the section to take a break — Section ${nextNumber}'s timer starts only when you press Begin.`
                    : sharedClock
                      ? "The clock keeps running."
                      : `The timer starts when you press "Begin Section ${nextNumber}".`}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {sectionAnswers.map((ans) => (
            <ReviewCard
              key={ans.position}
              answer={ans}
              questionnaireStartsAt={questionnaireStartsAt}
              allowJumpBack={canJumpBack}
              onJumpBack={() => navigate(`/t/${token}/question/${ans.position}`)}
            />
          ))}
        </div>
      </main>

      <footer
        className={cn(
          "sticky bottom-0 border-t border-border bg-card px-6 py-3",
          tileShown && CAMERA_TILE_FOOTER_CLEAR,
        )}
      >
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3">
          {saveFailures.failures.length > 0 ? (
            <span className="text-sm text-destructive">
              Retry the unsaved answers to continue.
            </span>
          ) : !allAnswered ? (
            <span className="text-sm text-muted-foreground">
              {unsubmitted} question{unsubmitted === 1 ? "" : "s"} still pending
              in this section.
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              All questions in this section submitted.
            </span>
          )}
          <div className="flex items-center gap-4">
            <ReportProblemTrigger token={token} attemptId={attemptId} />
            <Button
              type="button"
              onClick={onPrimary}
              disabled={busy || saveFailures.failures.length > 0}
              className="cursor-pointer"
            >
              {primaryLabel}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
